import { useState, useCallback } from "react"
import cn from "classnames"
import Image from "components/Image"
import useWindowSize from "utilities/useWindowSize"
import Arrow from "public/assets/circle-arrow.svg"
import Close from "public/assets/pawprint-x.svg"
import getSbImageDimensions from "utilities/getSbImageDimensions"

export default function PortfolioModal({ images, closeModal, focusRef }) {
  const [selectedImage, setSelectedImg] = useState(0)

  const { width } = useWindowSize()
  const visibleSlides = useCallback(() => {
    if (width >= 1536 && images.length >= 7) return 7
    if (width >= 1536 && images.length >= 6) return 6
    if (width >= 1024 && images.length >= 5) return 5
    return 1
  }, [images, width])

  const [currentSlide, setSlide] = useState(0)
  if (!images?.length) return null

  const slideWidth = 141

  function moveSlidesLeft() {
    if (currentSlide !== 0) {
      setSlide(currentSlide - slideWidth)
      setSelectedImg(selectedImage - 1)
    } else {
      if (selectedImage === 0) {
        setSlide(images.length * slideWidth - visibleSlides() * slideWidth)
        setSelectedImg(images.length - 1)
      } else {
        setSelectedImg(selectedImage - 1)
      }
    }
  }

  function moveSlidesRight() {
    if (currentSlide <= (images.length - 1 - visibleSlides()) * slideWidth) {
      setSlide(currentSlide + slideWidth)
      setSelectedImg(selectedImage + 1)
    } else {
      if (selectedImage === images.length - 1) {
        setSlide(0)
        setSelectedImg(0)
      } else {
        setSelectedImg(selectedImage + 1)
      }
    }
  }

  return (
    <>
      <div
        onClick={() => closeModal()}
        className="overlay fixed w-full h-full top-0 left-0 right-0 bottom-0 z-[9998] bg-black bg-opacity-50"
        aria-hidden
      />
      <div
        tabIndex={0}
        ref={focusRef}
        className="modal opacity-0 animate-fade-in fixed top-0 sm:top-1/2 sm:left-1/2 transform sm:-translate-x-1/2 sm:-translate-y-1/2 z-[9999] w-full px-8 lg:max-w-screen-lg 2xl:max-w-[1320px] max-h-[100dvh] overflow-y-auto overflow-x-clip"
      >
        <div className="bg-white rounded-b-3xl lg:rounded-t-3xl lg:block w-full my-10 2xl:my-16 lg:py-8 2xl:py-12 lg:px-6 relative">
          <button
            onClick={() => closeModal()}
            aria-label="Close Modal"
            title="Close Modal"
            className="close-modal-btn absolute -top-16 -right-8 2xl:-top-20 2xl:-right-2 z-10 scale-[66%] 2xl:scale-100"
          >
            <Close />
          </button>

          <div className="flex flex-col">
            <div className="flex gap-4 flex-col lg:flex-row mx-auto items-center w-full lg:w-auto mb-4 lg:mb-8">
              {images[selectedImage]?.before?.filename ? (
                <div className="relative mx-auto sm:w-max">
                  <Image
                    src={images[selectedImage].before.filename}
                    alt={images[selectedImage].before.alt}
                    placeholder={images[selectedImage]?.before?.blurDataURL ? "blur" : "empty"}
                    blurDataURL={images[selectedImage]?.before?.blurDataURL}
                    width={getSbImageDimensions("width", images[selectedImage].before.filename)}
                    height={442}
                    sizes="50vw"
                    className="sm:h-[442px] lg:h-[300px] 2xl:h-[442px] max-h-[442px] sm:w-auto object-left-top object-cover sm:object-contain lg:object-cover mx-auto"
                  />
                  <div className="absolute uppercase bg-green-sea rounded-t-3xl py-4 px-9 text-black font-bold leading-none text-s1 bottom-0 left-0">
                    Before
                  </div>
                </div>
              ) : null}
              <div className="relative lg:max-w-2xl mx-auto sm:w-max">
                <Image
                  src={images[selectedImage].after.filename}
                  alt={images[selectedImage].after.alt}
                  placeholder={images[selectedImage].after.blurDataURL ? "blur" : "empty"}
                  blurDataURL={images[selectedImage].after.blurDataURL}
                  width={getSbImageDimensions("width", images[selectedImage].after.filename)}
                  height={442}
                  sizes="50vw"
                  className="sm:h-[442px]  lg:h-[300px] 2xl:h-[442px] sm:w-auto object-left-top object-cover sm:object-contain lg:object-cover mx-auto"
                />
                {images[selectedImage]?.before?.filename ? (
                  <div className="absolute uppercase bg-green-sea rounded-t-3xl py-4 px-9 text-black font-bold leading-none text-s1 bottom-0 left-0">
                    After
                  </div>
                ) : null}
              </div>
            </div>
            <div className="px-4 lg:px-0 lg:static flex flex-col-reverse lg:flex-col">
              {images[selectedImage]?.heading ? (
                <h2 className="text-m1 leading-normal lg:text-m2 2xl:text-l1 text-center max-w-4xl mx-auto mb-4 2xl:pb-8">
                  {images[selectedImage]?.heading}
                </h2>
              ) : null}
              <div className="lg:relative">
                <div className="hidden lg:flex justify-center relative">
                  <div
                    className="flex flex-row translate-all items-center gap-x-2.5 duration-500"
                    style={{
                      transform: `translateX(-${currentSlide}px)`,
                      width: `${slideWidth * visibleSlides()}px`,
                    }}
                  >
                    {images.map((beforeAndAfter, idx) => {
                      const offset = currentSlide / slideWidth
                      const range = offset + visibleSlides()
                      const visible = idx < range && idx >= offset

                      return (
                        <button
                          className={cn(
                            "relative transition-all duration-300 w-full h-full flex-1 lg:w-[131px] lg:h-[87px] min-w-max",
                            {
                              "opacity-1 translate-y-0": visible,
                              "opacity-0 translate-y-10": !visible,
                            }
                          )}
                          key={beforeAndAfter?.after?.filename}
                          disabled={!visible}
                          onClick={() => setSelectedImg(idx)}
                        >
                          <Image
                            src={beforeAndAfter?.after.filename}
                            alt={beforeAndAfter?.after.alt}
                            placeholder={beforeAndAfter?.after?.blurDataURL ? "blur" : "empty"}
                            blurDataURL={beforeAndAfter?.after?.blurDataURL}
                            width={131}
                            height={87}
                            sizes="(max-width: 1024px) 95vw, 10vw"
                            className="object-cover w-full h-full object-top aspect-[131/87]"
                          />
                        </button>
                      )
                    })}
                  </div>
                </div>
                {images?.length > 1 ? (
                  <div className="gap-4 flex mx-auto w-max pb-4 lg:p-0 lg:m-0">
                    <button
                      className="lg:absolute lg:top-1/2 lg:-translate-y-1/2 left-0 lg:left-2"
                      onClick={() => moveSlidesLeft()}
                    >
                      <Arrow className="rotate-180" />
                    </button>
                    <button
                      className="lg:absolute lg:top-1/2 lg:-translate-y-1/2 right-0 lg:right-2"
                      onClick={() => moveSlidesRight()}
                    >
                      <Arrow />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
